import React, { useState } from 'react'
import {
  Button,
  Row,
  Col,
  Form,
  FileDropzone,
  CenteredSpinner
} from 'react-angle-dashboard-components'
import { NotFoundCard, SmallTitle, FormLabel, FormControl } from '../styles'
import { useMutation } from 'react-query'
import { PhoneControl } from '../../../components/PhoneControl/PhoneControl'
import { claimsService } from '../../../services'

const NoResultTicketForm = (
  {
    searchQuery,
    clearResults
  } :
  {
    searchQuery: any,
    clearResults: (msg: string) => void
  }) => {
  const [email, setEmail] = useState('')
  const [fax, setFax] = useState('')
  const [phone, setPhone] = useState('')
  const [unfoundClaimContext, setUnfoundClaimContext] = useState('')
  const [unfoundClaimFiles, setUnfoundClaimFiles] = useState<File[]>([])
  const [unfoundClaimResponseType, setUnfoundClaimResponseType] = useState<
    'email' | 'fax'
  >('email')
  const [fileDropzoneKey, setFileDropzoneKey] = useState(1)
  const [requestorName, setRequestorName] = useState('')
  const [phoneKey, setPhoneKey] = useState(1)
  const [faxKey, setFaxKey] = useState(1)

  const createTicket = useMutation((data: FormData) => {
    return claimsService.createNoResultTicket(data)
  })

  const clearAllInputs = () => {
    setEmail('')
    setFax('')
    setPhone('')
    setUnfoundClaimContext('')
    setUnfoundClaimFiles([])
    setUnfoundClaimResponseType('email')
    setFileDropzoneKey((k) => k + 1)
    setRequestorName('')
    setPhoneKey((k) => k + 1)
    setFaxKey((k) => k + 1)
    clearResults("Thank you, we'll be in touch soon!")
  }

  const onSubmit = async (e: any) => {
    e.preventDefault()

    const data: any = {}

    if (unfoundClaimResponseType === 'email') {
      data.contact = {
        email
      }
    } else {
      data.contact = {
        fax
      }
    }

    if (phone) {
      data.contact.phone = phone
    }

    data.criteria = searchQuery

    data.issue = {
      context: unfoundClaimContext,
      type: unfoundClaimResponseType,
      reason: 'Cant find claim'
    }

    data.requestor_name = requestorName

    const formData = new FormData()

    formData.append('data', JSON.stringify(data))

    if (unfoundClaimFiles) {
      unfoundClaimFiles.forEach((f) => {
        formData.append('file', f, f.name)
      })
    }

    try {
      await createTicket.mutateAsync(formData)
      clearAllInputs()
    } catch(e) {
      console.error(e)
    }
  }

  return (
    <>
      <NotFoundCard>
        <h5>
          We do not have a claim on file that matches the information you
          entered
        </h5>
        <p className="text-muted">
          If you believe that all of the information you entered is correct, the
          most common reason for this no matching claim message is that the
          claim submission form was rejected due to non-matching member details
          such as name or date of birth. To search for another claim, update the
          form fields above.
        </p>
      </NotFoundCard>
      <SmallTitle style={{ marginTop: 44 }}>
        Can't find your claim? Submit a ticket below.
      </SmallTitle>
      <Form onSubmit={onSubmit}>
        <Row style={{ paddingRight: 15, paddingLeft: 15, marginBottom: 15 }}>
          <FormLabel>
            Please provide context for your inquiry, including: NPI/TIN, claim
            number, billed amount, member ID, member DOB, or any other pieces of
            relevant information.
          </FormLabel>
          <Form.Control
            as="textarea"
            className="bg-white w-100"
            required
            onChange={(e: any) => setUnfoundClaimContext(e.target.value)}
            value={unfoundClaimContext}
          />
        </Row>
        <Row style={{ marginBottom: 15 }}>
          <Col>
            <FormLabel>Upload Documents/Files</FormLabel>
            <FileDropzone
              key={fileDropzoneKey}
              onFileLoaded={(e: File[]) => {
                setUnfoundClaimFiles(e)
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6} xs={12}>
            <Form.Group>
              <FormLabel>How would you like to receive our response?</FormLabel>
              <Form.Control
                as="select"
                className="bg-white"
                onChange={(e: any) =>
                  setUnfoundClaimResponseType(e.target.value)
                }
                value={unfoundClaimResponseType}
              >
                <option value="email">Email</option>
                <option value="fax">Fax number</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col sm={6} xs={12}>
            <Form.Group>
              <FormLabel>Requestor name</FormLabel>
              <FormControl
                required
                onChange={(e: any) => setRequestorName(e.target.value)}
                value={requestorName}
                placeholder="Please enter a requestor name"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm={6} xs={12}>
            {unfoundClaimResponseType === 'email' ? (
              <Form.Group>
                <FormLabel>Email</FormLabel>
                <FormControl
                  required
                  onChange={(e: any) => {
                    return setEmail(e.target.value)
                  }}
                  value={email}
                  placeholder="Please enter an email address"
                />
              </Form.Group>
            ) : (
              <Form.Group>
                <FormLabel>Fax number</FormLabel>
                <PhoneControl
                  required
                  key={faxKey}
                  onChange={(value: string) => setFax(value)}
                  placeholder="Please enter a fax number"
                />
              </Form.Group>
            )}
          </Col>
          <Col sm={6} xs={12}>
            {' '}
            <Form.Group>
              <FormLabel>Phone (optional)</FormLabel>
              <PhoneControl
                key={phoneKey}
                onChange={(value: string) => setPhone(value)}
                placeholder="Please enter a phone number"
              />
            </Form.Group>
          </Col>
        </Row>{' '}
        {createTicket.isLoading ? (
          <CenteredSpinner />
        ) : (
          <Button className="my-3 py-2" type="submit">
            Submit
          </Button>
        )}
        {createTicket.isError && (
          <Col
            className="mt-5 py-3 px-4 fs-20"
            sm={6}
            xs={12}
            style={{
              background: 'rgba(237, 76, 120, 0.1)',
              borderRadius: 6
            }}
          >
            {' '}
            An unexpected error occurred creating your ticket. Please reach out
            to our Care Team by phone at +1 (855) 937-1811.
          </Col>
        )}
      </Form>
    </>
  )
}

export default NoResultTicketForm
